import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Send, Plus, Trash2, Clock, CheckCircle2, XCircle, RotateCcw, CalendarClock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";

export default function AdminJobPoster() {
  const [data, setData] = useState(null);
  const [hookLabel, setHookLabel] = useState("");
  const [hookUrl, setHookUrl] = useState("");
  const [tpl, setTpl] = useState(null);
  const [days, setDays] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/jobpost/overview").then((r) => {
      setData(r.data);
      setTpl((t) => t || r.data.template);
      setDays(r.data.schedule_days);
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(ok || r.data?.message || "Done");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!data || !tpl) return <p className="font-mono text-[10px] text-zinc-600 animate-pulse p-4">Loading Job Poster…</p>;

  return (
    <section data-testid="admin-jobposter-section" className="flex flex-col gap-4">
      <div className="border border-white/10 bg-black/40">
        <div className="border-b border-white/10 px-6 py-4">
          <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-zinc-100 flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-nc-orange" /> Auto Job Poster — Discord Recruitment
          </h2>
          <p className="font-mono text-[10px] text-zinc-500 mt-2 leading-relaxed">
            Blasts the Creator Forge recruitment post to Discord servers via webhooks. Add webhooks from your own servers,
            partner communities and job-board servers that grant you posting access, then fire manually or let the scheduler auto-repost.
          </p>
        </div>
        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Template + send */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <p className={labelCls}>Recruitment post (Discord embed)</p>
              <button data-testid="jobpost-template-reset" onClick={() => setTpl(data.default_template)} className="font-mono text-[9px] uppercase text-zinc-500 hover:text-white flex items-center gap-1 transition-colors">
                <RotateCcw className="w-3 h-3" /> Reset to default
              </button>
            </div>
            <div>
              <label className={labelCls}>Title</label>
              <input data-testid="jobpost-title-input" className={inputCls} value={tpl.title} onChange={(e) => setTpl((t) => ({ ...t, title: e.target.value }))} maxLength={200} />
            </div>
            <div>
              <label className={labelCls}>Body (Discord markdown)</label>
              <textarea data-testid="jobpost-body-input" rows={10} className={`${inputCls} resize-y leading-relaxed`} value={tpl.description} onChange={(e) => setTpl((t) => ({ ...t, description: e.target.value }))} maxLength={3900} />
            </div>
            <div>
              <label className={labelCls}>Apply link</label>
              <input data-testid="jobpost-url-input" className={inputCls} value={tpl.url} onChange={(e) => setTpl((t) => ({ ...t, url: e.target.value }))} maxLength={300} />
            </div>
            <div className="flex gap-2">
              <button data-testid="jobpost-template-save" disabled={busy} onClick={() => act(() => api.put("/admin/jobpost/template", tpl))} className="font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 border border-white/15 text-zinc-300 hover:border-white/40 transition-colors">Save Template</button>
              <button data-testid="jobpost-send-button" disabled={busy || data.webhooks.filter((w) => w.enabled).length === 0} onClick={() => act(() => api.post("/admin/jobpost/send"))} className="flex-1 bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-2.5 flex items-center justify-center gap-2 transition-colors">
                <Send className="w-3.5 h-3.5" /> Blast to {data.webhooks.filter((w) => w.enabled).length} Server(s)
              </button>
            </div>
            <div className="border border-white/10 bg-[#050505]/60 p-3 flex items-center gap-3 flex-wrap">
              <CalendarClock className="w-4 h-4 text-nc-blue shrink-0" />
              <span className="font-mono text-[10px] text-zinc-400">Auto-repost every</span>
              <input data-testid="jobpost-schedule-input" type="number" min="0" max="30" className={`${inputCls} w-20 text-center`} value={days} onChange={(e) => setDays(parseInt(e.target.value) || 0)} />
              <span className="font-mono text-[10px] text-zinc-400">days (0 = off)</span>
              <button data-testid="jobpost-schedule-save" disabled={busy} onClick={() => act(() => api.put("/admin/jobpost/schedule", { every_days: days }))} className="ml-auto font-mono text-[9px] uppercase px-3 py-1.5 border border-nc-blue/50 text-nc-blue hover:bg-nc-blue/10 transition-colors">Set</button>
            </div>
            {data.last_sent && <p className="font-mono text-[9px] text-zinc-600">Last blast: {String(data.last_sent).slice(0, 16).replace("T", " ")}</p>}
          </div>

          {/* Webhooks + history */}
          <div className="flex flex-col gap-4">
            <div className="border border-white/10 bg-[#050505]/50 p-4 flex flex-col gap-3">
              <p className="font-mono text-[9px] tracking-[0.25em] text-nc-blue uppercase">Discord webhooks ({data.webhooks.length})</p>
              <div className="grid grid-cols-[140px_1fr_auto] gap-2">
                <input data-testid="jobpost-hook-label" className={inputCls} placeholder="Server label" value={hookLabel} onChange={(e) => setHookLabel(e.target.value)} maxLength={60} />
                <input data-testid="jobpost-hook-url" className={inputCls} placeholder="https://discord.com/api/webhooks/…" value={hookUrl} onChange={(e) => setHookUrl(e.target.value)} />
                <button data-testid="jobpost-hook-add" disabled={busy || hookLabel.trim().length < 2 || !hookUrl.includes("discord")} onClick={() => act(async () => { await api.post("/admin/jobpost/webhooks", { label: hookLabel, url: hookUrl }); setHookLabel(""); setHookUrl(""); })} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white px-3 transition-colors"><Plus className="w-4 h-4" /></button>
              </div>
              {data.webhooks.length === 0 && <p className="font-mono text-[10px] text-zinc-600">No webhooks yet. In Discord: Server Settings → Integrations → Webhooks → copy URL. Job-board servers that grant recruitment posting work great.</p>}
              {data.webhooks.map((w, i) => (
                <div key={w.id} data-testid={`jobpost-hook-${i + 1}`} className="flex items-center justify-between gap-3 border border-white/5 bg-black/40 px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <input type="checkbox" data-testid={`jobpost-hook-toggle-${i + 1}`} checked={w.enabled} onChange={(e) => act(() => api.patch(`/admin/jobpost/webhooks/${w.id}`, { enabled: e.target.checked }))} className="w-3.5 h-3.5 accent-[#2E6BFF]" />
                    <span className="font-mono text-[10px] text-zinc-200 truncate">{w.label}</span>
                  </div>
                  <button data-testid={`jobpost-hook-delete-${i + 1}`} disabled={busy} onClick={() => act(() => api.delete(`/admin/jobpost/webhooks/${w.id}`))} className="text-zinc-500 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>

            <div className="border border-white/10 bg-[#050505]/50 p-4 flex flex-col gap-2">
              <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase flex items-center gap-2"><Clock className="w-3 h-3" /> Post History</p>
              {data.recent_posts.length === 0 && <p data-testid="jobpost-history-empty" className="font-mono text-[10px] text-zinc-600">No posts sent yet.</p>}
              {data.recent_posts.map((p, i) => (
                <div key={p.id} data-testid={`jobpost-post-${i + 1}`} className="flex items-center justify-between border-b border-white/5 py-2">
                  <div className="flex items-center gap-2">
                    {p.failed_count === 0 ? <CheckCircle2 className="w-3.5 h-3.5 text-nc-green" /> : <XCircle className="w-3.5 h-3.5 text-nc-orange" />}
                    <span className="font-mono text-[10px] text-zinc-300 truncate max-w-[260px]">{p.template_title}</span>
                  </div>
                  <span className="font-mono text-[9px] text-zinc-500 shrink-0">
                    {p.sent_count} sent{p.failed_count > 0 ? ` / ${p.failed_count} failed` : ""}{p.auto ? " // auto" : ""} // {String(p.created_at).slice(0, 16).replace("T", " ")}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
